import "./StockModal.scss";
import FormField from "../../../../components/formField/FormField";
import { useState, useMemo, useEffect } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import StockModal from "./StockModal";
import PosIconModal from "../../../../components/AddProducts/PosIconModal";
import { CategoriesArray } from "../../../../utility/data";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../../firebase";

const DEFAULT_FORM_DATA = {
  name: "",
  customIcon: CategoriesArray[0]?.name,
};

export default function CreateStockCategoryModal({ open, onClose, onCreated, venueId, category }) {
  const { t } = useTranslation("stockManagement");
  const alert = useAlert();
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [iconModalOpen, setIconModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!category?.id;
  const stockCategoryValidationSchema = useMemo(
    () =>
      yup.object({
        name: yup.string().trim().required(t("verification_stock_category_name_required")),
        customIcon: yup.string().trim().required(t("verification_stock_category_icon_required")),
      }),
    [t],
  );
  const selectedIcon = CategoriesArray.find((icon) => icon.name === formData?.customIcon);

  useEffect(() => {
    if (open && isEditing) {
      setFormData({
        name: category?.name || "",
        customIcon: category?.customIcon || CategoriesArray[0]?.name,
      });
    } else if (open) {
      setFormData(DEFAULT_FORM_DATA);
    }
  }, [open, isEditing, category]);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      setErrors({});
      const payload = {
        name: formData.name.trim(),
        customIcon: formData.customIcon.trim(),
      };

      await stockCategoryValidationSchema.validate(payload, { abortEarly: false });

      if (isEditing) {
        await updateDoc(doc(db, "StockCategories", category.id), payload);
      } else {
        await addDoc(collection(db, "StockCategories"), { ...payload, venueId });
      }

      setFormData(DEFAULT_FORM_DATA);
      await onCreated();
    } catch (err) {
      if (err?.name === "ValidationError") {
        const formattedErrors = {};
        err.inner.forEach((error) => (formattedErrors[error.path] = error.message));
        setErrors(formattedErrors);
      } else {
        alert.show(
          t(
            isEditing
              ? "edit_stock_category_modal_submit_error"
              : "create_stock_category_modal_submit_error",
          ),
          { type: "error" },
        );
        console.error(`Error ${isEditing ? "updating" : "creating"} stock category:`, err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateHandler = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setFormData(DEFAULT_FORM_DATA);
    setErrors({});
    onClose();
  };

  return (
    <StockModal
      open={open}
      onClose={closeModal}
      onConfirm={handleConfirm}
      isLoading={isSubmitting}
      title={isEditing ? t("stock_overview_action_edit") : t("create_stock_category_modal_title")}
    >
      <div className="stock-action-modal-products">
        <p className="stock-action-modal-products-subtitle">
          {t(isEditing ? "edit_stock_category_modal_subtitle" : "create_stock_category_modal_subtitle")}
        </p>

        <FormField
          label={t("create_stock_category_modal_name")}
          value={formData.name}
          error={errors.name}
          onChange={(value) => updateHandler("name", value)}
        />

        <div className="fieldGroup stock-category-icon-selector-container">
          <label>{t("create_stock_category_modal_icon")}</label>
          <button
            type="button"
            className="stock-category-icon-selector form-control"
            onClick={() => setIconModalOpen(true)}
          >
            {selectedIcon?.icon && <img src={selectedIcon.icon} alt={formData.customIcon} />}
          </button>
          {errors.customIcon && <p className="errorMessage">{errors.customIcon}</p>}
        </div>
      </div>

      <PosIconModal
        modalOpen={iconModalOpen}
        setModalOpen={setIconModalOpen}
        customIcon={formData?.customIcon}
        setCustomIcon={(value) => updateHandler("customIcon", value)}
        selectedCategory="new"
      />
    </StockModal>
  );
}
